import type { StateCreator } from 'zustand'
import { BRANDING } from '~/config/branding'
import type { AppStore } from '../useStore'

const STORAGE_KEY = 'mmm-v3:personal'

export interface PersonalSlice {
  brandColor: string
  chipPalette: string[]
  setBrandColor: (c: string) => void
  setChipPalette: (p: string[]) => void
  reset: () => void
}

interface PersistedShape {
  brandColor: string
  chipPalette: string[]
}

/** The pre-wave-1 default (5-entry). A stored palette equal to it means the user
 *  never actually customized — upgrade them to the tenant palette (spec §4.2). */
const LEGACY_DEFAULT_CHIP_PALETTE = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd']

function isLegacyDefault(p: string[] | undefined): boolean {
  return (
    Array.isArray(p) &&
    p.length === LEGACY_DEFAULT_CHIP_PALETTE.length &&
    p.every((c, i) => c === LEGACY_DEFAULT_CHIP_PALETTE[i])
  )
}

function load(): PersistedShape {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return {
        brandColor: BRANDING.defaultBrandColor,
        chipPalette: [...BRANDING.defaultChipPalette],
      }
    }
    const parsed = JSON.parse(raw) as Partial<PersistedShape>
    return {
      brandColor: parsed.brandColor ?? BRANDING.defaultBrandColor,
      chipPalette:
        parsed.chipPalette && !isLegacyDefault(parsed.chipPalette)
          ? parsed.chipPalette
          : [...BRANDING.defaultChipPalette],
    }
  } catch {
    return {
      brandColor: BRANDING.defaultBrandColor,
      chipPalette: [...BRANDING.defaultChipPalette],
    }
  }
}

function save(s: PersistedShape) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
}

export const createPersonalSlice: StateCreator<AppStore, [], [], { personal: PersonalSlice }> = (
  set,
  get,
) => {
  const initial = load()
  return {
    personal: {
      ...initial,
      setBrandColor: (brandColor) => {
        set((s) => ({ personal: { ...s.personal, brandColor } }))
        save({ brandColor, chipPalette: get().personal.chipPalette })
      },
      setChipPalette: (chipPalette) => {
        set((s) => ({ personal: { ...s.personal, chipPalette } }))
        save({ brandColor: get().personal.brandColor, chipPalette })
      },
      reset: () => {
        localStorage.removeItem(STORAGE_KEY)
        set((s) => ({
          personal: {
            ...s.personal,
            brandColor: BRANDING.defaultBrandColor,
            chipPalette: [...BRANDING.defaultChipPalette],
          },
        }))
      },
    },
  }
}
